import React, { forwardRef } from 'react';
import clsx from 'clsx';

/**
 * Card — surface primitive.
 *
 * Variants:
 *   surface   — default white card with subtle border + soft shadow
 *   soft      — muted cream card, no shadow
 *   glass     — translucent blur (for over-image areas)
 *   elevated  — bigger shadow for floating prominence
 *   sunken    — inset / quiet area within another card
 *   flat      — no shadow, no border (for plain content blocks)
 *
 * Padding: none, xs, sm, md, lg, xl
 * Hover:   none, lift, elevate, glow
 */
const VARIANTS = {
    surface:  'bg-surface border border-line shadow-card',
    soft:     'bg-surface-muted border border-line-subtle',
    glass:    'bg-white/75 backdrop-blur-md backdrop-saturate-150 border border-white/60 shadow-soft',
    elevated: 'bg-surface border border-line shadow-elevated',
    sunken:   'bg-surface-sunken border border-line-subtle',
    flat:     'bg-surface',
};

const PADDING = {
    none: '',
    xs:   'p-2.5',
    sm:   'p-3.5',
    md:   'p-5',
    lg:   'p-6',
    xl:   'p-8',
};

const HOVER = {
    none:    '',
    lift:    'transition-transform duration-250 ease-smooth hover:-translate-y-0.5',
    elevate: 'transition-[transform,box-shadow] duration-250 ease-smooth hover:-translate-y-0.5 hover:shadow-elevated',
    glow:    'transition-shadow duration-250 ease-smooth hover:shadow-focus',
};

export const Card = forwardRef(function Card({
    children,
    className,
    variant = 'surface',
    padding = 'md',
    interactive = false,
    hover = 'elevate',
    radius = 'lg',
    onClick,
    as: Component = 'div',
    ...rest
}, ref) {
    const radiusClass = {
        sm: 'rounded-md', md: 'rounded-lg', lg: 'rounded-lg', xl: 'rounded-xl', '2xl': 'rounded-2xl', '3xl': 'rounded-3xl',
    }[radius] || 'rounded-lg';

    return (
        <Component
            ref={ref}
            onClick={onClick}
            className={clsx(
                VARIANTS[variant] || VARIANTS.surface,
                radiusClass,
                PADDING[padding] ?? PADDING.md,
                interactive && 'cursor-pointer',
                interactive ? HOVER[hover] : '',
                className,
            )}
            {...rest}
        >
            {children}
        </Component>
    );
});

export function CardHeader({ children, className, divider = true }) {
    return (
        <div className={clsx(
            'mb-4 pb-4',
            divider && 'border-b border-line-subtle',
            className,
        )}>
            {children}
        </div>
    );
}

export function CardTitle({ children, className, as: Component = 'h3' }) {
    return (
        <Component className={clsx('heading-md', className)}>
            {children}
        </Component>
    );
}

export function CardDescription({ children, className }) {
    return (
        <p className={clsx('text-sm text-ink-muted mt-1', className)}>
            {children}
        </p>
    );
}

export function CardContent({ children, className, spacing = 'md' }) {
    const spacingClass = {
        none: '',
        sm:   'space-y-2',
        md:   'space-y-3',
        lg:   'space-y-5',
    }[spacing] || 'space-y-3';

    return <div className={clsx(spacingClass, className)}>{children}</div>;
}

export function CardFooter({ children, className, divider = true, align = 'end' }) {
    const alignClass = {
        start:   'justify-start',
        center:  'justify-center',
        end:     'justify-end',
        between: 'justify-between',
    }[align] || 'justify-end';

    return (
        <div className={clsx(
            'mt-4 pt-4 flex items-center gap-3',
            divider && 'border-t border-line-subtle',
            alignClass,
            className,
        )}>
            {children}
        </div>
    );
}

export default Card;
